'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/Accordion';
import { useTabContext } from '../context/TabContext';
import { CustomerFooter } from '@/components/layout/CustomerFooter';
import { AdviserFooter } from '@/components/layout/AdviserFooter';

export default function Footer() {
  const { selectedTab } = useTabContext();
  const footerLinks = selectedTab === 'adviser' ? adviserFooterLinks : customerFooterLinks;

  return (
    <footer
      className={`${
        selectedTab === 'adviser' ? 'bg-[#212C55]' : 'bg-[#4f1030]'
      } text-white`}
    >
      {selectedTab === 'adviser'
        ? <div className="bg-[#006EB7] px-4 py-3 text-center footer-disclaimer">
          *This website is not intended or approved for customer use.{' '}
          <Link href="/" className="text-[#F3F4F8] font-semibold underline decoration-[#C1E0F5]">
            Return to our customer website.
          </Link>
        </div>
        : <div className="bg-[#f3f4f8] text-[#373945] px-4 py-3 text-center footer-disclaimer">
          This website provides information on lifetime mortgages. To understand the features and risks, ask for a personalised illustration.
        </div>
      }

      <div className="container py-12 mx-auto lg:py-16">
        {/* Desktop Navigation */}
        <div className={`hidden lg:grid lg:grid-cols-6 lg:gap-8 ${selectedTab === 'customer' ? 'xl:gap-12' : ''}`}>
          <div className="col-span-2 lg:mr-10">
            <div className="-mt-5">
              <Link href="/">
                <Image
                  src="/img/footer-logo.svg"
                  alt="Logo"
                  className="w-32 h-[4.8125rem] md:w-32 md:h-[4.8125rem] lg:w-32 lg:h-[4.8125rem]"
                  width={128}
                  height={77}
                  loading="lazy"
                />
              </Link>
            </div>
            <div className="footer-default-text mt-5 w-[11.375rem]">
                We’re a Leeds-based mortgage lender, committed to first class customer service.
            </div>
            <div className="mt-5 flex footer-default-text">
              <img src="/img/icons/marker-pin-01.svg" className="w-5 h-5 mr-2" alt="Marker Pin" loading="lazy" />
              <span className="w-[9.81rem]">2200 Century Way, Leeds, LS15 8ZB</span>
            </div>

            <div className="flex items-center gap-4 mt-5">
              <div>
                <Link href="https://www.equityreleasecouncil.com" target="_blank">
                  <img src="/img/erc-1.webp" className="" alt="Equity" loading="lazy"/>
                </Link>
              </div>
              <div className="rounded-md bg-[#FFF] w-[6rem] h-[5rem] justify-center items-center inline-flex">
                <div className="relative">
                  <Link href="https://puremediastorage.blob.core.windows.net/pure-web-media/general_process/fsqs-certificate.pdf" target="_blank">
                    <img src="/img/img_6.webp" alt="Logo" loading="lazy"/>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          {selectedTab === 'adviser' ? <AdviserFooter footerLinks={footerLinks} /> : <CustomerFooter />}
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden">
          <div className="-mt-5">
            <Link href="/">
              <Image
                src="/img/footer-logo.svg"
                alt="Logo"
                className="w-32 h-[4.8125rem] md:w-32 md:h-[4.8125rem] lg:w-32 lg:h-[4.8125rem]"
                width={128}
                height={77}
                priority
              />
            </Link>
          </div>
          <div className="footer-default-text mt-5 w-[15rem]">
            We’re a Leeds-based mortgage lender, committed to first class customer service.
          </div>
          <div className="mt-5 flex footer-default-text">
            <img src="/img/icons/marker-pin-01.svg" className="w-5 h-5 mr-2" alt="Marker Pin" loading="lazy" />
            <span>2200 Century Way, Leeds, LS15 8ZB</span>
          </div>

          <div className="flex items-center gap-4 mt-5">
            <div>
              <Link href="https://www.equityreleasecouncil.com" target="_blank">
                <img src="/img/erc-1.webp" alt="Equity" loading="lazy"/>
              </Link>
            </div>
            <div className="rounded-md bg-[#FFF] w-[6rem] h-[5rem] justify-center items-center inline-flex">
              <div className="relative">
                <Link href="https://puremediastorage.blob.core.windows.net/pure-web-media/general_process/fsqs-certificate.pdf" target="_blank">
                  <img src="/img/img_6.webp" alt="Logo" loading="lazy"/>
                </Link>
              </div>
            </div>
          </div>
          <Accordion type="single" collapsible className="mt-5">
            {footerLinks.map((section) =>
              section.isAccordion ? (
                <AccordionItem
                  key={section.title}
                  value={section.title}
                >
                  <AccordionTrigger
                    className="flex items-center justify-between py-3 footer-accordion-title" isMobile={true}>
                    {section.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul>
                      {section.links.map((link) => (
                        <li key={link.text}>
                          <Link
                            href={link.href}
                            className="footer-accordion-content transition-colors pt-3 pb-3"
                          >
                            {link.text}
                          </Link>
                        </li>
                      ))}
                      {section.socialLinks && (
                        <li className="flex items-center gap-4 mt-4">
                          {section.socialLinks.map((link) => (
                            <Link href={link.href} key={link.icon}>
                              <img
                                src={`/img/icons/${link.icon}.svg`}
                                alt={link.icon}
                                className="w-4 h-4"
                              />
                            </Link>
                          ))}
                        </li>
                      )}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ) : (
                <div key={section.title}>
                  <Link
                    href={section.href}
                    className="flex items-center justify-between py-3 footer-accordion-title hover:underline"
                  >
                    {section.title}
                  </Link>
                  <ul>
                    {section.links.map((link) => (
                      <li key={link.text}>
                        <Link
                          href={link.href}
                          className="footer-accordion-content pt-3 pb-3"
                        >
                          {link.text}
                        </Link>
                      </li>
                    ))}
                    {section.socialLinks && (
                      <li className="flex items-center gap-4 mt-4">
                        {section.socialLinks.map((link) => (
                          <Link href={link.href} key={link.icon}>
                            <img src={`/img/icons/${link.icon}.svg`} className="w-6 h-6" alt="Social Icon"/>
                          </Link>
                        ))}
                      </li>
                    )}
                  </ul>
                </div>
              )
            )}
          </Accordion>
        </div>

        <div className="mt-8 lg:mt-36 flex flex-col footer-links items-center justify-center text-center">
          <div className="flex flex-wrap justify-center">
            <span className="flex items-center footer-policy">© 2025 Pure Retirement</span>
            <span className="mx-2">|</span>
            {legalLinks.map((link, index) => (
              <span key={link.text} className="flex items-center footer-policy">
                <a
                  href={
                    link.text === 'Complaints Procedure' && selectedTab === 'adviser'
                      ? '/professionals/about-us/complaints-procedure'
                      : link.href
                  }
                  className="hover:text-white hover:underline"
                >
                  {link.text}
                </a>
                {index < legalLinks.length - 1 && <span className="mx-2">|</span>}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

const adviserFooterLinks = [
  {
    title: 'Lifetime Mortgages',
    href: '/professionals/lifetime-mortgages',
    links: [
      { text: 'Lending Criteria', href: '/professionals/lifetime-mortgages/lending-criteria' },
      { text: 'Servicing Solutions', href: '/professionals/lifetime-mortgages/servicing-solutions' },
      { text: 'Interest Servicing', href: '/professionals/lifetime-mortgages/interest-servicing' },
      { text: 'Create KFI', href: 'https://adviserportal.pureretirement.co.uk/my/create-kfi' },
    ],
    isAccordion: true,
  },
  {
    title: 'Resources',
    href: '/professionals/resources',
    links: [
      { text: 'Academy', href: '/professionals/resources/academy' },
      { text: 'Marketing Toolkit', href: '/professionals/resources/marketing-toolkit' },
      { text: 'Adviser Tools', href: '/professionals/resources/adviser-support-tools' },
      { text: 'Customer Tools', href: '/professionals/resources/customer-support-tools' },
    ],
    isAccordion: true,
  },
  {
    title: 'About Us',
    href: '/professionals/about-us',
    links: [
      { text: 'Lending Mission', href: '/professionals/about-us/our-responsible-lending' },
      { text: 'Our CSR', href: '/professionals/about-us/our-corporate-social-responsibility' },
      { text: 'Careers', href: '/careers' },
      { text: 'News', href: '/professionals/news/insight-blog' },
      { text: 'Events', href: '/professionals/events' },
    ],
    isAccordion: true,
  },
  {
    title: 'Contact',
    href: '/professionals/contact-us',
    links: [{ text: 'Find My BDM', href: '/professionals/lifetime-mortgages/find-my-bdm' }],
    socialLinks: [
      { icon: 'youtube-white', href: 'https://www.youtube.com/channel/UCB9qhCS5ZWArD1hiZF0P48g' },
      { icon: 'linkedin-white', href: 'https://www.linkedin.com/company/pure-retirement-ltd' },
    ],
    isAccordion: false,
  },
];

const customerFooterLinks = [
  {
    title: 'Equity Release',
    href: '/equity-release',
    links: [
      { text: 'What Is Equity Release?', href: '/equity-release/what-is-equity-release' },
      { text: 'How Does Equity Release work?', href: '/equity-release/how-does-equity-release-work' },
      { text: 'Definitive Equity Release Glossary', href: '/equity-release/definitive-equity-release-glossary' },
    ],
    isAccordion: true,
  },
  {
    title: 'Knowledge Corner',
    href: '/knowledge-corner',
    links: [
      { text: 'Your FAQs Answered', href: '/knowledge-corner/equity-release-faqs' },
    ],
    isAccordion: true,
  },
  {
    title: 'Existing Customers',
    href: '/existing-customers',
    links: [
      { text: 'Existing Customers FAQs', href: '/existing-customers/existing-customers-faqs' },
    ],
    isAccordion: true,
  },
  {
    title: 'About Us',
    href: '/about-us',
    links: [
      { text: 'Responsible Business', href: '/about-us/responsible-business' },
      { text: 'Careers', href: '/careers' },
    ],
    isAccordion: true,
  },
  {
    title: 'MyPure Login',
    href: 'https://customer.pureretirement.co.uk',
    links: [
      { text: 'Contact Us', href: '/contact-us' },
    ],
    socialLinks: [
      { icon: 'youtube-white', href: 'https://www.youtube.com/channel/UCB9qhCS5ZWArD1hiZF0P48g ' },
    ],
    isAccordion: true,
  },
];

const legalLinks = [
  { text: 'Privacy Policy', href: '/privacy-policy' },
  { text: 'Complaints Procedure', href: '/complaints-procedure' },
  { text: 'Cookie Policy', href: '/cookie-policy' },
  { text: 'Gender Pay Report', href: '/gender-pay-report' },
  { text: 'Anti-Slavery Statement', href: '/anti-slavery-statement' },
];
